import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance, AxiosResponse } from 'axios';
import { type ApiError, addActionNameToMutationResult, useAxiosInstance } from 'shared/hooks/utils';
import { z } from 'zod/v4';
import { StepTypeSchema } from './step-type';
import { TemplateTypeSchema } from './use-load-templates';

const CreateTemplateVariablesSchema = z.object({
  name: z.string(),
  stepType: StepTypeSchema,
  templateType: TemplateTypeSchema,
  withFees: z.boolean().optional(),
});

export type CreateTemplateVariables = z.infer<typeof CreateTemplateVariablesSchema>;

type CreateTemplateResponse = AxiosResponse<{ id: number }>;

async function createTemplateFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: CreateTemplateVariables,
): Promise<CreateTemplateResponse> {
  const body = CreateTemplateVariablesSchema.parse(variables);
  const instance = await axiosInstance;
  return instance.post('/templates', body);
}

export function useCreateTemplate() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<CreateTemplateResponse, ApiError, CreateTemplateVariables>({
    mutationFn: variables => createTemplateFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  return addActionNameToMutationResult<
    CreateTemplateResponse,
    ApiError,
    CreateTemplateVariables,
    'createTemplate'
  >('createTemplate', mutation);
}
